import { createContext, useEffect, useContext, ReactNode, useRef, useCallback, useState, useMemo } from 'react';
import createPortalRealtimeClient, {
  PortalRealtimeClientType,
  CreateRealtimeClientType,
} from './client'; // Adjust the path as needed
import { NextRouter } from 'next/router';

// Define the shape of your context
export type PortalRealtimeContextType = PortalRealtimeClientType | null;

let singletonPortalRealtimeClient: PortalRealtimeClientType | null = null;

export const getSingletonPortalRealtimeClient = (
  serverUrl: string,
  options?: CreateRealtimeClientType<any, any>['options'],
  stateCallback?: CreateRealtimeClientType<any, any>['stateCallback']
): PortalRealtimeClientType => {
  if (!singletonPortalRealtimeClient) {
    singletonPortalRealtimeClient = createPortalRealtimeClient(serverUrl, options, stateCallback);
  }
  return singletonPortalRealtimeClient;
};

// Create the context
export const PortalRealtimeContext = createContext<PortalRealtimeContextType>(null);

export type SessionObjectType = {
  status: 'authenticated' | 'unauthenticated';
  data: {
    accessToken: string;
    user: {
      group: string;
    }
  }
};

export interface PortalRealtimeProviderProps {
  children?: ReactNode;
  serverEnv: 'local' | 'dev' | 'prod' | 'localhost' | 'development' | 'production';
  router?: NextRouter;
  options?: CreateRealtimeClientType<any, any>['options'],
  stateCallback?: CreateRealtimeClientType<any, any>['stateCallback']
  disabled: boolean;
}

import { useSession } from 'next-auth/react';

const _emptyClient: PortalRealtimeClientType = {
  isLoggedIn: () => { console.log('isLoggedIn.emptyClient'); return false; },
  login: () => { console.log('login.emptyClient') },
  logout: () => { console.log('logout.emptyClient') },
  authenticate: () => { console.log('authenticate.emptyClient') },
  onRouteChanged: () => { console.log('onRouteChanged.emptyClient') },
  onDeleteGlobalFieldValue: () => { console.log('onDeleteGlobalFieldValue.emptyClient')},
  ping: () => { console.log('ping.emptyClient') },
 // getAvailabilityData: (tagKey: string) => { console.log('getAvailabilityData.emptyClient'); return Promise.resolve({}); },
};

export const PortalRealtimeProvider = ({
  children,
  serverEnv,
  disabled,
  router,
  options,
  stateCallback,
}: PortalRealtimeProviderProps) => {
  // console.log('disabled', disabled);
  const sessionObject = useSession();
  const portalRealtimeClientRef = useRef<PortalRealtimeClientType | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const baseServerUrls = {
    'local': 'http://localhost:3663',
    'dev': 'https://dev-api.sprakservice.se',
    'prod': 'https://api.sprakservice.se',
  }
  const serverUrls = {
    ...baseServerUrls,
    localhost: baseServerUrls.local,
    production: baseServerUrls.prod,
    development: baseServerUrls.dev,
  }

  const serverUrl = serverUrls[serverEnv];

  // Initialize client only once and keep it persistent
  useEffect(() => {
    if (!disabled && !portalRealtimeClientRef.current) {
      portalRealtimeClientRef.current = getSingletonPortalRealtimeClient(serverUrl, options, stateCallback);
      setIsInitialized(true);
    } else if (disabled) {
      setIsInitialized(true);
    }
  }, [disabled, serverUrl, options, stateCallback]);

  // Handle cleanup only when truly needed
  useEffect(() => {
    if (disabled && portalRealtimeClientRef.current) {
      portalRealtimeClientRef.current.logout();
      portalRealtimeClientRef.current = null;
    }

    return () => {
      if (portalRealtimeClientRef.current && !singletonPortalRealtimeClient) {
        portalRealtimeClientRef.current.logout();
        portalRealtimeClientRef.current = null;
      }
    };
  }, [disabled]);

  useEffect(() => {
    if (!router) {
      // If `router` is not available, return a no-op cleanup function
      return () => { };
    }

    const handleRouteChange = (url?: string) => {
      const client = portalRealtimeClientRef.current;
      if (!client) return;
      if (client) {
        client.onRouteChanged();
      }
    };

    const buildHandleRouteWrapper = (name: string) => {
      return (url?: string) => {
        console.log('route event type:', name);
        handleRouteChange(url);
      };
    };
    const routeChangeStart = buildHandleRouteWrapper('routeChangeStart');
    const routeChangeComplete = buildHandleRouteWrapper('routeChangeComplete');
    const routeChangeError = buildHandleRouteWrapper('routeChangeError');
    // Attach the event listeners
    router.events.on('routeChangeStart', routeChangeStart);
    router.events.on('routeChangeComplete', routeChangeComplete);
    router.events.on('routeChangeError', routeChangeError);

    // Clean up the event listeners on component unmount
    return () => {
      if (router) {
        router.events.off('routeChangeStart', routeChangeStart);
        router.events.off('routeChangeComplete', routeChangeComplete);
        router.events.off('routeChangeError', routeChangeError);
      }
    };
  }, [router]);

  useEffect(() => {
    if (disabled) {
      const client = portalRealtimeClientRef.current;
      if (client) {
        client.logout();
        portalRealtimeClientRef.current = null;
      }
      return () => { };
    }
    const client = portalRealtimeClientRef.current;
    // console.log('sessionObject', sessionObject);
    if ((!sessionObject || sessionObject?.status === 'unauthenticated') && client) {
      const _isLoggedIn = client.isLoggedIn();
      // console.log('_isLoggedIn', _isLoggedIn);
      if (_isLoggedIn) {
        client?.logout();
      }
      // If `router` is not available, return a no-op cleanup function
      return () => { };
    }

    // @ts-ignore
    const _accessToken = sessionObject?.data?.accessToken;
    // @ts-ignore
    if (client && _accessToken) {
      const _isLoggedIn = client.isLoggedIn();
      if (!_isLoggedIn) {
        console.log('provider will log user in!');
        client.authenticate(_accessToken);
      }
    }
    return () => {
      if (client) {
        client.logout();
      }
    }
    // @ts-ignore
  }, [sessionObject, disabled]);

  // Determine the context value - always provide a stable reference
  const contextValue = useMemo(() => {
    if (disabled) {
      return _emptyClient;
    }
    return portalRealtimeClientRef.current || _emptyClient;
  }, [disabled, isInitialized]);

  return (
    <PortalRealtimeContext.Provider value={contextValue}>
      {children}
    </PortalRealtimeContext.Provider>
  );
}

// Custom hook for using the Realtime context
export const usePortalRealtime = () => {
  const context = useContext(PortalRealtimeContext);

  if (context === null) {
    console.warn('usePortalRealtime: No PortalRealtimeProvider found. Using empty client.');
    return _emptyClient;
  }

  // if (!context) {
  //   throw new Error('usePortalRealtime must be used within a PortalRealtimeProvider');
  // }
  return context;
};

// export const useAvailabilityData = () => {
//   const client = usePortalRealtime();

//   const fetchAvailabilityData = useCallback(async (tagKey: string) => {
//     return await client.getAvailabilityData(tagKey);
//   }, [client]);

//   return {
//     fetchAvailabilityData
//   }
// };